import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TruckIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { customerService, OrderDetail } from '../services/customerService';
import { Badge } from '../../../components';
import { Spinner } from '../../../components';
import { formatDate, formatCurrency } from '../../../lib/utils';
import { useAuthStore } from '../../../stores';

interface OrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  orderId: string | null;
}

function getStatusVariant(status: string) {
  switch (status) {
    case 'delivered':
    case 'paid':
    case 'fulfilled':
      return 'success';
    case 'shipped':
    case 'confirmed':
    case 'partially_fulfilled':
    case 'partially_paid':
    case 'authorized':
      return 'info';
    case 'processing':
    case 'pending':
    case 'on_hold':
    case 'unfulfilled':
      return 'warning';
    case 'cancelled':
    case 'refunded':
    case 'partially_refunded':
    case 'failed':
    case 'voided':
      return 'danger';
    default:
      return 'default';
  }
}

function formatStatus(status: string) {
  return status.replace(/_/g, ' ');
}

export default function OrderDetailModal({ open, onClose, customerId, orderId }: OrderDetailModalProps) {
  const storeCurrency = useAuthStore((s) => s.currency);

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order-detail', customerId, orderId],
    queryFn: () => customerService.getOrder(customerId, orderId!),
    enabled: !!orderId && open,
    retry: false,
  });

  const currency = order?.currency || storeCurrency;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-panel bg-surface border border-line shadow-l">
                {isLoading ? (
                  <div className="flex justify-center py-16">
                    <Spinner />
                  </div>
                ) : isError || !order ? (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-ink-3">Order not found or could not be loaded.</p>
                    <button
                      onClick={onClose}
                      className="mt-3 text-sm font-medium text-brand hover:text-brand-ink"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <OrderContent order={order} currency={currency} onClose={onClose} />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function OrderContent({ order, currency, onClose }: { order: OrderDetail; currency: string; onClose: () => void }) {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-6 py-4">
        <div>
          <Dialog.Title className="text-lg font-semibold text-ink">
            Order #{order.orderNumber}
          </Dialog.Title>
          <p className="mt-0.5 text-sm text-ink-3">{formatDate(order.createdAt)}</p>
        </div>
        <button onClick={onClose} className="rounded-xl p-1 text-ink-2 hover:bg-black/[0.04]">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-4 scrollbar-thin">
        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          <Badge variant={getStatusVariant(order.status)} size="sm">
            {formatStatus(order.status)}
          </Badge>
          <Badge variant={getStatusVariant(order.paymentStatus)} size="sm">
            {formatStatus(order.paymentStatus)}
          </Badge>
          <Badge variant={getStatusVariant(order.fulfillmentStatus)} size="sm">
            {formatStatus(order.fulfillmentStatus)}
          </Badge>
        </div>

        {/* Line items */}
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink">Items</h4>
          <div className="divide-y divide-line rounded-card border border-line">
            {order.items.map((item, i) => (
              <div key={i} className="flex items-start justify-between px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">{item.productName}</p>
                  {item.variantName && (
                    <p className="text-xs text-ink-3">{item.variantName}</p>
                  )}
                  <p className="text-xs text-ink-3">
                    {formatCurrency(item.price, currency)} x {item.quantity}
                  </p>
                </div>
                <p className="ml-3 text-sm font-medium text-ink whitespace-nowrap">
                  {formatCurrency(item.total, currency)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink">Summary</h4>
          <div className="space-y-1.5 rounded-card border border-line px-3 py-2.5 text-sm">
            <div className="flex justify-between text-ink-2">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal, currency)}</span>
            </div>
            {order.tax > 0 && (
              <div className="flex justify-between text-ink-2">
                <span>Tax</span>
                <span>{formatCurrency(order.tax, currency)}</span>
              </div>
            )}
            {order.shipping > 0 && (
              <div className="flex justify-between text-ink-2">
                <span>Shipping</span>
                <span>{formatCurrency(order.shipping, currency)}</span>
              </div>
            )}
            {order.discount > 0 && (
              <div className="flex justify-between text-success">
                <span>Discount</span>
                <span>-{formatCurrency(order.discount, currency)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-line pt-1.5 font-semibold text-ink">
              <span>Total</span>
              <span>{formatCurrency(order.total, currency)}</span>
            </div>
          </div>
        </div>

        {/* Tracking */}
        {(order.trackingNumber || order.trackingUrl) && (
          <div>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
              <TruckIcon className="h-4 w-4" />
              Tracking
            </h4>
            <div className="rounded-card border border-line px-3 py-2.5 text-sm">
              {order.trackingNumber && (
                <p className="text-ink-2">
                  <span className="text-ink-3">Number: </span>
                  {order.trackingNumber}
                </p>
              )}
              {order.trackingUrl && (
                <a
                  href={order.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-brand hover:text-brand-ink hover:underline"
                >
                  Track shipment
                </a>
              )}
            </div>
          </div>
        )}

        {/* Shipping address */}
        {order.shippingAddress && (
          <div>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
              <MapPinIcon className="h-4 w-4" />
              Shipping Address
            </h4>
            <div className="rounded-card border border-line px-3 py-2.5 text-sm text-ink-2 leading-relaxed">
              <p>{order.shippingAddress.first_name} {order.shippingAddress.last_name}</p>
              <p>{order.shippingAddress.address1}</p>
              {order.shippingAddress.address2 && <p>{order.shippingAddress.address2}</p>}
              <p>
                {order.shippingAddress.city}
                {order.shippingAddress.province && `, ${order.shippingAddress.province}`}
                {' '}{order.shippingAddress.zip}
              </p>
              <p>{order.shippingAddress.country}</p>
            </div>
          </div>
        )}

        {/* Notes */}
        {order.notes && (
          <div>
            <h4 className="mb-2 text-sm font-semibold text-ink">Notes</h4>
            <p className="rounded-card border border-line px-3 py-2.5 text-sm text-ink-2">
              {order.notes}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
